import { createContext, PropsWithChildren, useCallback, useEffect, useState } from 'react';

import { initI18n } from '../i18n';
import { API_V1 } from '../services/api.service';
import { getHostDomain } from '../utils/get-host-domain';
import { getSessionToken } from '../utils/get-session-token';
import { getCheckoutToken } from '../utils/get-checkout-token';
import { initProductEventsService } from '../utils/mixpanel-utils';
import { initCoralogixRum } from '../utils/coralogix-rum';
import { FeatureFlags, CouponsConfiguration, PublisherData } from '../contracts';
import { appThemeAdaptar, defaultFeatureFlags, getInitialConfig } from '../utils/boot-utils';

export interface AppConfig {
  theme: Theme;
  publisherId: string;
  supportEmail: string;
  featureFlags?: FeatureFlags;
  couponsConfiguration: CouponsConfiguration;
  isDefaultBoot: boolean;
  isBootInitialized: boolean;
  publisherData: PublisherData;
}

export interface Theme {
  logo: string;
  backgroundImage?: string;
  mobileBackgroundImage?: string;
  button: {
    color: string;
    textColor: string;
  };
  text: string;
  primary: string;
}

export const AppConfigContext = createContext<Partial<AppConfig> | null>(null);

export const AppConfigProvider = ({ children }: PropsWithChildren) => {
  const [config, setConfig] = useState<Partial<AppConfig>>(getInitialConfig);
  const [isBootInitialized, setIsBootInitialized] = useState(!config.isDefaultBoot);
  const urlParams = new URLSearchParams(window.location.search);
  const cot = urlParams.get('cot');

  useEffect(() => {
    initProductEventsService();
  }, []);

  useEffect(() => {
    const sessionToken = getSessionToken();
    if (sessionToken) {
      initI18n(sessionToken);
    }
  }, []);

  const fetchFallbackBoot = useCallback(() => {
    const domain = cot ? '' : getHostDomain();
    const checkoutToken = cot || getCheckoutToken();

    API_V1.getBoot(domain, checkoutToken)
      .then((data) => {
        const appTheme = appThemeAdaptar(data?.checkoutTheme);
        const supportEmail = data.supportEmail;
        setConfig((prevConfig) => ({
          ...prevConfig,
          theme: appTheme,
          publisherId: data.checkoutTheme.publisherId,
          couponsConfiguration: data.couponsConfiguration,
          supportEmail,
          featureFlags: data.featureFlags,
        }));
      })
      .catch((err) => {
        console.error(err);
        setConfig((prevConfig) => ({
          ...prevConfig,
          featureFlags: defaultFeatureFlags,
        }));
      })
      .finally(() => setIsBootInitialized(true));
  }, []);

  useEffect(() => {
    if (!isBootInitialized) {
      fetchFallbackBoot();
    }
  }, [isBootInitialized, fetchFallbackBoot]);

  useEffect(() => {
    if (isBootInitialized) {
      initCoralogixRum(config as AppConfig);
    }
  }, [isBootInitialized]);

  return (
    <AppConfigContext.Provider value={{ ...config, isBootInitialized }}>
      {children}
    </AppConfigContext.Provider>
  );
};
